import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'tg-portal-theme';

/**
 * Portal (authenticated app) theme manager — matches the public brand with a
 * light/dark toggle. Default is light.
 *
 * Because Radix UI portals render to document.body, dark mode MUST be applied
 * as a class on <html> so dialogs/selects/dropdowns inherit the theme.
 * The 'dark' class is removed on unmount so it never leaks onto public pages.
 */
export function usePortalTheme() {
  const [theme, setThemeState] = useState(() => {
    if (typeof window === 'undefined') return 'light';
    return window.localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    const el = document.documentElement;
    if (theme === 'dark') el.classList.add('dark');
    else el.classList.remove('dark');
    return () => { el.classList.remove('dark'); };
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* noop */ }
  }, []);

  const toggle = useCallback(() => {
    setThemeState((cur) => {
      const next = cur === 'dark' ? 'light' : 'dark';
      try { window.localStorage.setItem(STORAGE_KEY, next); } catch (e) { /* noop */ }
      return next;
    });
  }, []);

  return { theme, setTheme, toggle };
}
